/**
 * Product-sync job hooks (Phase 65 / BLU-594 PR3b).
 *
 * Server-state only (mirrors `useReconcile`): the job lives in
 * `org_product_sync_jobs` and we poll it — no local status mirror, so a reload
 * resumes from the row. Polls every 2s while the job is active
 * (queued/planning/awaiting_approval/running), plus while a completed job's
 * durable inventory coverage is pending, and otherwise stops at terminal.
 * Phase 92: a completed job whose detached supply resync has not reported yet
 * keeps polling, bounded by `finished_at` + the resync's own lease wait
 * (`productSyncJobPollMs`); a job without supply counters polls as before.
 * `stats` is narrowed to `unknown` at the query boundary to sever the recursive
 * `Json` type (TS2589); `asJobStats` narrows at use.
 *
 * The PLAN is deliberately NOT part of that poll — at the 500-product job cap
 * it is multi-MB, and Thread Lab reaches this page through the HK proxy. It has
 * its own fetch-once query (`useProductSyncJobPlan`).
 */
import { useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc';
import {
  productSyncJobPollMs,
  type ProductSyncJobStatus,
  type WireJob,
} from '../lib/apply-types';

/** Poll one job. Pass `null` to disable. */
export function useProductSyncJob(jobId: string | null) {
  return trpc.productSync.getProductSyncJob.useQuery(
    { jobId: jobId ?? '' },
    {
      enabled: !!jobId,
      // Param annotated `unknown` so TS never expands the recursive `Json`
      // result type (TS2589); the coercers narrow it downstream.
      select: (row: unknown) => (row ?? null) as WireJob | null,
      refetchInterval: (query) => {
        const job = query.state.data as WireJob | null | undefined;
        return job ? productSyncJobPollMs(job) : false;
      },
    },
  );
}

/** The two states whose UI needs the plan: the approval gate, and the run's denominator. */
function needsPlanFor(status: ProductSyncJobStatus | null | undefined): boolean {
  return status === 'awaiting_approval' || status === 'running';
}

/**
 * The stored plan for ONE job — fetched ONCE, never polled (`staleTime:
 * Infinity`). Only enabled for the states that actually need it, so a
 * queued/planning job never pulls a plan that doesn't exist yet and a terminal
 * job never re-pulls one the backend has already nulled out (`jobs.dao.finish`
 * clears `plan` for retention). Data already in the cache survives the
 * transition to a terminal state, so a run you watched finish keeps its
 * "N of N" denominator.
 *
 * Re-reads once on a genuine transition INTO a plan-bearing state, because the
 * stored plan CHANGES at approval (it is rewritten to the approved subset —
 * possibly by an admin in another tab). The first enable is not re-fetched: the
 * query has no data yet, so react-query is already fetching it.
 */
export function useProductSyncJobPlan(jobId: string | null, status: ProductSyncJobStatus | null | undefined) {
  const needsPlan = needsPlanFor(status);
  const query = trpc.productSync.getProductSyncJobPlan.useQuery(
    { jobId: jobId ?? '' },
    {
      enabled: !!jobId && needsPlan,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );

  const { refetch } = query;
  const hasPlanData = query.data !== undefined;
  const prevStatusRef = useRef<ProductSyncJobStatus | null>(null);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status ?? null;
    // First observation (prev === null) or a re-run for another reason: nothing to refresh.
    if (prev === null || prev === status) return;
    if (!needsPlanFor(status) || !hasPlanData) return;
    void refetch();
  }, [status, hasPlanData, refetch]);

  return query;
}

/** Recent runs for the quiet history affordance. */
export function useProductSyncJobs() {
  return trpc.productSync.listProductSyncJobs.useQuery(undefined, {
    select: (rows) => rows as unknown as WireJob[],
  });
}
